import { useEffect, useState } from "react";
import { useMutation, usePaginatedQuery } from "convex/react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { EditIcon, PlusIcon, SendIcon } from "../components/icons";
import { PageHeader } from "../components/shell";
import { Button, ConfirmationDialog, Field, Notice, Spinner } from "../components/ui";
import { describeOperatorError } from "../lib/errors";
import type { OrganisationSummary } from "./types";

type Selection =
  | { kind: "rename"; organisation: OrganisationSummary }
  | { kind: "resend"; organisation: OrganisationSummary }
  | null;

function formatCreatedAt(timestamp: number): string {
  return new Intl.DateTimeFormat("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(timestamp));
}

export function OrganisationsListView({
  organisations,
  loadingMore = false,
  onLoadMore,
  onRename,
  onResend,
  successMessage: initialSuccess = null,
}: {
  organisations: OrganisationSummary[];
  loadingMore?: boolean;
  onLoadMore?: () => void;
  onRename: (organisationId: Id<"organisations">, name: string) => Promise<void>;
  onResend: (organisationId: Id<"organisations">) => Promise<void>;
  successMessage?: string | null;
}) {
  const [selection, setSelection] = useState<Selection>(null);
  const [renameValue, setRenameValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(initialSuccess);

  function openRename(organisation: OrganisationSummary) {
    setError(null);
    setRenameValue(organisation.name);
    setSelection({ kind: "rename", organisation });
  }

  function closeDialog() {
    if (busy) return;
    setSelection(null);
    setError(null);
  }

  async function confirmRename() {
    if (!selection || selection.kind !== "rename" || busy) return;
    const name = renameValue.trim().replace(/\s+/g, " ");
    if (!name) {
      setError("Enter a new organisation name.");
      return;
    }
    if (name === selection.organisation.name) {
      setError("Enter a different organisation name.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const oldName = selection.organisation.name;
      await onRename(selection.organisation._id, name);
      setSuccessMessage(`Renamed ${oldName} to ${name}.`);
      setSelection(null);
    } catch (caught) {
      setError(describeOperatorError(caught, "rename"));
    } finally {
      setBusy(false);
    }
  }

  async function confirmResend() {
    if (!selection || selection.kind !== "resend" || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onResend(selection.organisation._id);
      setSuccessMessage(`Resent the pending first-administrator invitation for ${selection.organisation.name}.`);
      setSelection(null);
    } catch (caught) {
      setError(describeOperatorError(caught, "resend"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <PageHeader
        eyebrow="Organisation register"
        title="Organisations"
        actions={
          <Link className="button button--primary" to="/organisations/new">
            <PlusIcon /> New organisation
          </Link>
        }
      >
        Provision customer workspaces and maintain organisation identity. Customer operational data is not available here.
      </PageHeader>

      {successMessage ? (
        <Notice tone="success" title="Completed">{successMessage}</Notice>
      ) : null}

      {organisations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__mark" aria-hidden="true"><span /><span /><span /></div>
          <p className="eyebrow">Register empty</p>
          <h2>No organisations yet</h2>
          <p>Create the first customer workspace and nominate its first administrator.</p>
          <Link className="button button--primary" to="/organisations/new">
            <PlusIcon /> Create first organisation
          </Link>
        </div>
      ) : (
        <div className="register">
          <div className="register__head" aria-hidden="true">
            <span>Organisation</span><span>Created</span><span>First administrator</span><span>Actions</span>
          </div>
          <ol className="register__list">
            {organisations.map((organisation, index) => (
              <li key={organisation._id} className="register-row">
                <span className="register-row__index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <div className="register-row__name"><strong>{organisation.name}</strong><code>{organisation._id}</code></div>
                <time dateTime={new Date(organisation._creationTime).toISOString()}>{formatCreatedAt(organisation._creationTime)}</time>
                <div>
                  {organisation.canResendFirstAdministratorInvitation ? (
                    <span className="status-chip status-chip--pending">Invitation pending</span>
                  ) : (
                    <span className="status-chip status-chip--bound">Invitation unavailable</span>
                  )}
                </div>
                <div className="register-row__actions">
                  <Button tone="quiet" onClick={() => openRename(organisation)}><EditIcon /> Rename</Button>
                  {organisation.canResendFirstAdministratorInvitation ? (
                    <Button tone="quiet" onClick={() => { setError(null); setSelection({ kind: "resend", organisation }); }}>
                      <SendIcon /> Resend
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
          {onLoadMore ? (
            <div className="register__more">
              <Button tone="secondary" busy={loadingMore} onClick={onLoadMore}>Load more organisations</Button>
            </div>
          ) : null}
        </div>
      )}

      <ConfirmationDialog
        open={selection?.kind === "rename"}
        title="Confirm organisation rename"
        onClose={closeDialog}
        actions={
          <>
            <Button tone="quiet" disabled={busy} onClick={closeDialog}>Cancel</Button>
            <Button busy={busy} onClick={() => void confirmRename()}>Rename organisation</Button>
          </>
        }
      >
        {selection?.kind === "rename" ? (
          <>
            <Field label="Current name"><input value={selection.organisation.name} readOnly /></Field>
            <Field label="New name"><input data-dialog-initial-focus value={renameValue} onChange={(event) => setRenameValue(event.target.value)} /></Field>
            <p className="confirmation-copy">Rename <strong>{selection.organisation.name}</strong> to <strong>{renameValue.trim() || "the new name"}</strong>. Ownership, membership, and history will not change.</p>
            {error ? <Notice tone="danger">{error}</Notice> : null}
          </>
        ) : null}
      </ConfirmationDialog>

      <ConfirmationDialog
        open={selection?.kind === "resend"}
        title="Resend first-administrator invitation?"
        onClose={closeDialog}
        actions={
          <>
            <Button tone="quiet" disabled={busy} onClick={closeDialog}>Cancel</Button>
            <Button busy={busy} onClick={() => void confirmResend()}><SendIcon /> Resend invitation</Button>
          </>
        }
      >
        {selection?.kind === "resend" ? (
          <>
            <p>Schedule the existing onboarding invitation for <strong>{selection.organisation.name}</strong> again?</p>
            <Notice tone="warning">The recipient and administrator details cannot be viewed or changed from this console.</Notice>
            {error ? <Notice tone="danger">{error}</Notice> : null}
          </>
        ) : null}
      </ConfirmationDialog>
    </section>
  );
}

export function OrganisationsListRoute() {
  const pagination = usePaginatedQuery(api.organisations.list, {}, { initialNumItems: 20 });
  const rename = useMutation(api.organisations.rename);
  const resend = useMutation(api.organisations.resendFirstAdministratorInvitation);
  const location = useLocation();
  const navigate = useNavigate();
  const success = (location.state as { success?: unknown } | null)?.success;

  useEffect(() => {
    if (typeof success === "string") {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, navigate, success]);

  if (pagination.status === "LoadingFirstPage") {
    return <div className="route-loading"><Spinner label="Loading organisations" /><p>Loading organisation register…</p></div>;
  }

  return (
    <OrganisationsListView
      organisations={pagination.results}
      loadingMore={pagination.status === "LoadingMore"}
      onLoadMore={pagination.status === "CanLoadMore" ? () => pagination.loadMore(20) : undefined}
      onRename={async (organisationId, name) => { await rename({ organisationId, name }); }}
      onResend={async (organisationId) => { await resend({ organisationId }); }}
      successMessage={typeof success === "string" ? success : null}
    />
  );
}
